import { useEffect, useState } from 'react'
import { Card, Button, Input, SectionHeading, Divider, KVRow, Tabs } from '../../../shared/components/UI'
import { costingApi } from '../../../shared/lib/api'
import { getRows } from '../../../shared/lib/partyType'

const FALLBACK = '-'

export default function CostingPage() {
  const [tab, setTab] = useState('Config')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [yarnRates, setYarnRates] = useState([])
  const [metallicRates, setMetallicRates] = useState([])
  const [batchCosts, setBatchCosts] = useState([])
  const [config, setConfig] = useState({ yarnPct: 0, labourCutting: 0, labourBobbin: 0, labourSpindle: 0, labourBoiling: 0, labourCone: 0 })
  const [newRate, setNewRate]     = useState('')
  const [newRateDate, setNewRateDate] = useState('')
  const [newMRate, setNewMRate]   = useState('')
  const [newMRateDate, setNewMRateDate] = useState('')

  // Bobbin Types state
  const [bobbinTypes, setBobbinTypes] = useState([])
  const [showInactive, setShowInactive] = useState(false)
  const [newTypeName, setNewTypeName] = useState('')
  const [newTypeWeight, setNewTypeWeight] = useState('')
  const [btError, setBtError] = useState('')
  const [btSaving, setBtSaving] = useState(false)

  const c = (k) => (e) => setConfig(p => ({ ...p, [k]: e.target.value }))
  const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`

  const loadData = async () => {
    setLoading(true)
    try {
      const [configRes, ratesRes, mRatesRes, costsRes] = await Promise.all([
        costingApi.getConfig(),
        costingApi.listYarnRates(),
        costingApi.listMetallicRates(),
        costingApi.allBatchCosts(),
      ])

      const cfg = configRes?.data?.data || configRes?.data || {}
      setConfig({
        yarnPct:       cfg.yarn_pct || 0,
        labourCutting: cfg.labour_cutting || 0,
        labourBobbin:  cfg.labour_bobbin || 0,
        labourSpindle: cfg.labour_spindle || 0,
        labourBoiling: cfg.labour_boiling || 0,
        labourCone:    cfg.labour_cone || 0,
      })

      setYarnRates(getRows(ratesRes))
      setMetallicRates(getRows(mRatesRes))
      setBatchCosts(getRows(costsRes))
      setError('')
    } catch (err) {
      setError('Unable to load costing data.')
    } finally {
      setLoading(false)
    }
  }

  const loadBobbinTypes = async () => {
    try {
      const res = await costingApi.listBobbinTypes(showInactive)
      setBobbinTypes(getRows(res))
      setBtError('')
    } catch {
      setBtError('Unable to load bobbin types.')
    }
  }

  useEffect(() => { loadData() }, [])
  useEffect(() => { if (tab === 'Bobbin Types') loadBobbinTypes() }, [tab, showInactive])

  const avgMetallicRate = batchCosts.length > 0
    ? batchCosts.reduce((acc, b) => acc + (parseFloat(b.metallicCost || 0) / parseFloat(b.inputKg || 1)), 0) / batchCosts.length
    : 450 // Fallback to 450 if no data

  // Configured global metallic rate wins for the preview; fall back to the
  // observed average from batch costs when none is set yet.
  const currentMetallicRate = parseFloat(metallicRates[0]?.rate_per_kg) || avgMetallicRate
  const currentRate = yarnRates[0]?.rate_per_kg || 0
  const PREVIEW_WEIGHT = 96
  const metallicTotal = PREVIEW_WEIGHT * currentMetallicRate
  const yarnKg = (PREVIEW_WEIGHT * parseFloat(config.yarnPct || 0) / 100).toFixed(2)
  const yarnCost = parseFloat(yarnKg) * currentRate
  const labourPerKg = 
    parseFloat(config.labourCutting || 0) + 
    parseFloat(config.labourBobbin || 0) + 
    parseFloat(config.labourSpindle || 0) + 
    parseFloat(config.labourBoiling || 0) + 
    parseFloat(config.labourCone || 0)
  
  const labourTotal = PREVIEW_WEIGHT * labourPerKg
  const totalCostEstimate = metallicTotal + yarnCost + labourTotal

  const onAddYarnRate = async () => {
    if (!newRate || !newRateDate) return
    setSaving(true)
    try {
      await costingApi.addYarnRate({ 
        ratePerKg: parseFloat(newRate), 
        effectiveFrom: newRateDate 
      })
      setNewRate('')
      setNewRateDate('')
      await loadData()
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to add yarn rate.')
    } finally {
      setSaving(false)
    }
  }

  const onAddMetallicRate = async () => {
    if (!newMRate || !newMRateDate) return
    setSaving(true)
    try {
      await costingApi.addMetallicRate({
        ratePerKg: parseFloat(newMRate),
        effectiveFrom: newMRateDate
      })
      setNewMRate('')
      setNewMRateDate('')
      await loadData()
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to add metallic rate.')
    } finally {
      setSaving(false)
    }
  }

  const onAddBobbinType = async () => {
    if (!newTypeName.trim() || !newTypeWeight) return
    setBtSaving(true)
    try {
      await costingApi.addBobbinType({ name: newTypeName.trim(), weightGrams: parseFloat(newTypeWeight) })
      setNewTypeName('')
      setNewTypeWeight('')
      await loadBobbinTypes()
    } catch (err) {
      setBtError(err?.response?.data?.message || 'Unable to add bobbin type.')
    } finally {
      setBtSaving(false)
    }
  }

  const onToggleActive = async (bt) => {
    try {
      await costingApi.updateBobbinType(bt.id, { isActive: !bt.is_active })
      await loadBobbinTypes()
    } catch {
      setBtError('Unable to update bobbin type.')
    }
  }

  const onDeleteBobbinType = async (id) => {
    if (!window.confirm('Delete this bobbin type?')) return
    try {
      await costingApi.deleteBobbinType(id)
      await loadBobbinTypes()
    } catch {
      setBtError('Unable to delete bobbin type.')
    }
  }

  const onSaveConfig = async () => {
    setSaving(true)
    try {
      await costingApi.saveConfig({
        yarnPct:       parseFloat(config.yarnPct),
        labourCutting: parseFloat(config.labourCutting),
        labourBobbin:  parseFloat(config.labourBobbin),
        labourSpindle: parseFloat(config.labourSpindle),
        labourBoiling: parseFloat(config.labourBoiling),
        labourCone:    parseFloat(config.labourCone),
      })
      await loadData()
      alert('Configuration saved successfully!')
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to save configuration.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <Tabs tabs={['Config', 'Yarn Rates', 'Metallic Rates', 'Batch Costs', 'Bobbin Types']} active={tab} onChange={setTab} />

      {tab === 'Config' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card title="Labour Cost per Stage (₹ / KG)">
            {error && <div className="mb-4 text-xs text-red-600 bg-red-50 p-2 rounded">{error}</div>}
            <SectionHeading>Yarn</SectionHeading>
            <Input label="Yarn % of bobbin input weight" type="number" value={config.yarnPct} onChange={c('yarnPct')} hint="Yarn rate is set in the Yarn Rates tab." />
            <Divider />
            <SectionHeading>Labour per stage</SectionHeading>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Cutting"        type="number" value={config.labourCutting} onChange={c('labourCutting')} />
              <Input label="Bobbin & Batch" type="number" value={config.labourBobbin}  onChange={c('labourBobbin')} />
              <Input label="Spindle"        type="number" value={config.labourSpindle} onChange={c('labourSpindle')} />
              <Input label="Boiling"        type="number" value={config.labourBoiling} onChange={c('labourBoiling')} />
              <Input label="Cone Formation" type="number" value={config.labourCone}    onChange={c('labourCone')} />
            </div>
            <Button onClick={onSaveConfig} disabled={saving}>{saving ? 'Saving...' : 'Save Configuration'}</Button>
          </Card>
          <Card title="Live Preview — Standard Batch (96 KG)">
            <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-xs text-amber-700 mb-4">
              Current Yarn rate: <strong>₹{currentRate}/KG</strong> {yarnRates[0] && `(effective ${new Date(yarnRates[0].effective_from).toLocaleDateString()})`}
            </div>
            <KVRow label={`Metallic (96 KG × ${fmt(currentMetallicRate)}/KG)`} value={fmt(metallicTotal)} />
            <KVRow label={`Yarn (${yarnKg} KG × ₹${currentRate}/KG)`} value={fmt(yarnCost)} />
            <Divider />
            <KVRow label={`Cutting  (96 × ₹${config.labourCutting || 0})`}  value={fmt(96*(config.labourCutting || 0))} />
            <KVRow label={`Bobbin   (96 × ₹${config.labourBobbin || 0})`}   value={fmt(96*(config.labourBobbin || 0))} />
            <KVRow label={`Spindle  (96 × ₹${config.labourSpindle || 0})`}  value={fmt(96*(config.labourSpindle || 0))} />
            <KVRow label={`Boiling  (96 × ₹${config.labourBoiling || 0})`}  value={fmt(96*(config.labourBoiling || 0))} />
            <KVRow label={`Cone     (96 × ₹${config.labourCone || 0})`}     value={fmt(96*(config.labourCone || 0))} />
            <Divider />
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-semibold text-gray-800">Total Cost Estimate</span>
              <span className="text-lg font-semibold text-brand-600">{fmt(totalCostEstimate)}</span>
            </div>
          </Card>
        </div>
      )}

      {tab === 'Yarn Rates' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <Card title="Yarn Rate History" bodyClass="!p-0">
              <table className="data-table">
                <thead><tr><th>Rate / KG (₹)</th><th>Effective From</th><th>Entered By</th><th></th></tr></thead>
                <tbody>
                  {yarnRates.length > 0 ? (
                    yarnRates.map((yr, i) => (
                      <tr key={yr.id}>
                        <td className="font-semibold text-gray-800 text-base">₹{yr.rate_per_kg}</td>
                        <td className="text-gray-600 text-sm">{new Date(yr.effective_from).toLocaleDateString()}</td>
                        <td className="text-gray-400 text-xs">{yr.creator?.name || FALLBACK}</td>
                        <td>{i === 0 && <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Current</span>}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="4" className="text-center py-8 text-gray-400 italic">No yarn rate history.</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </div>
          <Card title="Add Yarn Rate">
            <div className="bg-brand-50 rounded-lg px-3 py-2 text-xs text-brand-700 mb-4">
              Batches closed on or after the effective date will use the new rate. Historical batches are unaffected.
            </div>
            <Input label="New rate per KG (₹)" value={newRate} onChange={(e) => setNewRate(e.target.value)} type="number" placeholder="e.g. 260" />
            <Input label="Effective from" value={newRateDate} onChange={(e) => setNewRateDate(e.target.value)} type="date" />
            <Button onClick={onAddYarnRate} disabled={saving || !newRate || !newRateDate}>{saving ? 'Adding...' : 'Add Yarn Rate'}</Button>
          </Card>
        </div>
      )}

      {tab === 'Metallic Rates' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <Card title="Metallic Rate History" bodyClass="!p-0">
              <table className="data-table">
                <thead><tr><th>Rate / KG (₹)</th><th>Effective From</th><th>Entered By</th><th></th></tr></thead>
                <tbody>
                  {metallicRates.length > 0 ? (
                    metallicRates.map((mr, i) => (
                      <tr key={mr.id}>
                        <td className="font-semibold text-gray-800 text-base">₹{mr.rate_per_kg}</td>
                        <td className="text-gray-600 text-sm">{new Date(mr.effective_from).toLocaleDateString()}</td>
                        <td className="text-gray-400 text-xs">{mr.enteredBy?.name || FALLBACK}</td>
                        <td>{i === 0 && <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Current</span>}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="4" className="text-center py-8 text-gray-400 italic">No metallic rate history.</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </div>
          <Card title="Add Metallic Rate">
            <div className="bg-brand-50 rounded-lg px-3 py-2 text-xs text-brand-700 mb-4">
              The standard metallic price per KG. Used for batch costing only when a roll has no rate of its own. Batches closed on or after the effective date use the new rate; past batches are unaffected.
            </div>
            <Input label="New rate per KG (₹)" value={newMRate} onChange={(e) => setNewMRate(e.target.value)} type="number" placeholder="e.g. 450" />
            <Input label="Effective from" value={newMRateDate} onChange={(e) => setNewMRateDate(e.target.value)} type="date" />
            <Button onClick={onAddMetallicRate} disabled={saving || !newMRate || !newMRateDate}>{saving ? 'Adding...' : 'Add Metallic Rate'}</Button>
          </Card>
        </div>
      )}

      {tab === 'Batch Costs' && (
        <Card title="Batch Costs — Dynamic Yarn Rate" action={<Button variant="secondary" size="sm">Export</Button>} bodyClass="!p-0">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Batch</th><th>Closed</th><th>Yarn Rate Used</th><th>Metallic</th><th>Yarn</th><th>Labour</th><th>Total</th><th>Output KG</th><th>Cost/KG</th></tr></thead>
              <tbody>
                {batchCosts.length > 0 ? batchCosts.map((r) => (
                  <tr key={r.batchNumber}>
                    <td className="td-mono">{r.batchNumber}</td>
                    <td className="text-xs text-gray-500">{r.closedAt ? new Date(r.closedAt).toLocaleDateString() : FALLBACK}</td>
                    <td><span className="text-xs font-medium bg-amber-50 text-amber-700 px-2 py-0.5 rounded">Real-time</span></td>
                    <td>{fmt(r.metallicCost)}</td>
                    <td>{fmt(r.yarnCost || 0)}</td>
                    <td>{fmt(r.labourCost)}</td>
                    <td className="font-semibold">{fmt(r.totalCost)}</td>
                    <td>{r.outputKg}</td>
                    <td className={`font-semibold text-teal-600`}>₹{r.costPerKg}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="9" className="text-center py-8 text-gray-400 italic">No batch cost records found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
            Each batch uses the yarn rate active on its closing date. Rate changes don't affect past batches.
          </div>
        </Card>
      )}
      {tab === 'Bobbin Types' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <Card
              title="Bobbin Types"
              action={
                <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={e => setShowInactive(e.target.checked)}
                    className="rounded"
                  />
                  Show inactive
                </label>
              }
              bodyClass="!p-0"
            >
              {btError && (
                <div className="mx-4 mt-3 text-xs text-red-600 bg-red-50 p-2 rounded">{btError}</div>
              )}
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Weight (g)</th>
                    <th>Added by</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {bobbinTypes.length > 0 ? bobbinTypes.map(bt => (
                    <tr key={bt.id} className={!bt.is_active ? 'opacity-50' : ''}>
                      <td className="font-medium text-gray-800">{bt.name}</td>
                      <td className="text-gray-700 font-semibold">{bt.weight_grams} g</td>
                      <td className="text-xs text-gray-400">{bt.creator?.name || '—'}</td>
                      <td>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${bt.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                          {bt.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="flex gap-2 justify-end">
                        <button
                          onClick={() => onToggleActive(bt)}
                          className="text-xs text-brand-600 hover:underline"
                        >
                          {bt.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => onDeleteBobbinType(bt.id)}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="text-center py-8 text-gray-400 italic">No bobbin types added yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Card>
          </div>

          <Card title="Add Bobbin Type">
            <div className="bg-brand-50 rounded-lg px-3 py-2 text-xs text-brand-700 mb-4">
              Define the tare weight of each bobbin type. Workers can select the type when logging batches — weight is auto-calculated from bobbin count × type weight.
            </div>
            <Input
              label="Type name"
              value={newTypeName}
              onChange={e => setNewTypeName(e.target.value)}
              placeholder="e.g. Small Bobbin"
            />
            <Input
              label="Weight per bobbin (grams)"
              value={newTypeWeight}
              onChange={e => setNewTypeWeight(e.target.value)}
              type="number"
              placeholder="e.g. 84"
            />
            {newTypeWeight && newTypeName && (
              <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-xs text-amber-700 mb-3">
                Preview: 75 bobbins × {newTypeWeight} g = <strong>{((75 * parseFloat(newTypeWeight || 0)) / 1000).toFixed(3)} KG</strong>
              </div>
            )}
            <Button
              onClick={onAddBobbinType}
              disabled={btSaving || !newTypeName.trim() || !newTypeWeight}
            >
              {btSaving ? 'Adding...' : 'Add Bobbin Type'}
            </Button>
          </Card>
        </div>
      )}
    </div>
  )
}
